import { useEffect, useRef, useState } from "react";
import { ChevronDown, Menu, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CALENDLY_URL } from "@/lib/constants";
import { SERVICE_CATEGORIES, getServicesByCategory, type ServiceCategoryName } from "@/lib/services";
import logoWhite from "@/assets/finable-logo-white.svg";
import logoDark from "@/assets/finable-logo-dark.svg";

const aboutLinks = [
  { label: "Unser Team", to: "/team", desc: "Lerne die Köpfe hinter Finable kennen" },
  { label: "Kundenstimmen", to: "/kundenstimmen", desc: "Echte Stimmen unserer Mandant:innen" },
  { label: "Karriere", to: "/karriere", desc: "Werde Teil des FINABLE-Teams" },
  { label: "Blog", to: "/blog", desc: "Finanzwissen, das sich auszahlt" },
  { label: "Glossar", to: "/glossar", desc: "Finanzbegriffe einfach erklärt" },
];

type DropdownKey = "services" | "about" | null;

function CategoryColumn({ name }: { name: ServiceCategoryName }) {
  const items = getServicesByCategory(name);
  if (items.length === 0) return null;
  return (
    <div className="min-w-0">
      <div className="text-[11px] font-bold uppercase tracking-wider text-accent mb-1.5 px-2">
        {name}
      </div>
      <ul className="flex flex-col">
        {items.map((s) => {
          const Icon = s.icon;
          return (
            <li key={s.slug}>
              <Link
                to={`/leistungen/${s.slug}`}
                className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-secondary/60 transition-colors group"
              >
                <Icon className="w-4 h-4 text-muted-foreground group-hover:text-accent shrink-0" />
                <span className="text-sm font-medium text-foreground/85 group-hover:text-primary truncate">
                  {s.shortName}
                </span>
                {s.badge && (
                  <span
                    className="ml-auto shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white"
                    style={{ backgroundColor: "#4CAF50" }}
                  >
                    {s.badge}
                  </span>
                )}
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function Header({ alwaysSolid = false }: { alwaysSolid?: boolean }) {
  const [scrolledState, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<DropdownKey>(null);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const closeTimer = useRef<number | null>(null);
  const scrolled = alwaysSolid || scrolledState;
  const location = useLocation();

  useEffect(() => {
    if (alwaysSolid) return;
    let lastScrolled = window.scrollY > 12;
    setScrolled(lastScrolled);
    const onScroll = () => {
      const next = window.scrollY > 12;
      if (next !== lastScrolled) {
        lastScrolled = next;
        setScrolled(next);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [alwaysSolid]);

  useEffect(() => {
    setOpen(false);
    setOpenDropdown(null);
    setMobileServicesOpen(false);
    setMobileAboutOpen(false);
  }, [location.pathname]);

  const openMenu = (key: Exclude<DropdownKey, null>) => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    setOpenDropdown(key);
  };
  const scheduleClose = () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpenDropdown(null), 120);
  };

  const navLinkClass = `text-sm font-medium transition-colors ${
    scrolled ? "text-foreground/80 hover:text-accent" : "text-white/80 hover:text-white"
  }`;
  const dropdownTriggerClass = `flex items-center gap-1 text-sm font-medium transition-colors ${
    scrolled ? "text-foreground/80 hover:text-accent" : "text-white/80 hover:text-white"
  }`;

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-background/85 backdrop-blur-md border-b border-border shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="container flex items-center h-16 md:h-20">
        <Link to="/" className="flex items-center gap-2 mr-auto" aria-label="Finable Startseite">
          <img
            src={scrolled ? logoDark : logoWhite}
            alt="Finable – Unabhängige Finanzberatung Logo"
            width={120}
            height={36}
            className="h-8 md:h-9 w-auto"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-7 ml-auto">
          {/* 1. Start */}
          <Link to="/" className={navLinkClass}>
            Start
          </Link>

          {/* 2. Leistungen */}
          <div
            className="relative"
            onMouseEnter={() => openMenu("services")}
            onMouseLeave={scheduleClose}
          >
            <button
              type="button"
              onClick={() => setOpenDropdown((v) => (v === "services" ? null : "services"))}
              aria-expanded={openDropdown === "services"}
              aria-haspopup="true"
              className={dropdownTriggerClass}
            >
              Leistungen
              <ChevronDown
                className={`w-4 h-4 transition-transform ${openDropdown === "services" ? "rotate-180" : ""}`}
              />
            </button>

            {openDropdown === "services" && (
              <div
                className="absolute top-full left-1/2 -translate-x-1/2 pt-3"
                onMouseEnter={() => openMenu("services")}
                onMouseLeave={scheduleClose}
              >
                <div className="w-[min(95vw,780px)] rounded-2xl bg-card border border-border shadow-elegant overflow-hidden animate-fade-in">
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-3 p-4">
                    <div className="space-y-4 min-w-0">
                      <CategoryColumn name="Einkommensversicherung" />
                      <CategoryColumn name="Altersvorsorge & Vermögensaufbau" />
                    </div>
                    <div className="space-y-4 min-w-0">
                      <CategoryColumn name="Sachversicherungen" />
                      <CategoryColumn name="Gesundheitsvorsorge" />
                    </div>
                    <div className="space-y-4 min-w-0">
                      <CategoryColumn name="Gewerbeversicherung" />
                      <CategoryColumn name="Finanzierung" />
                    </div>
                  </div>
                  <Link
                    to="/leistungen"
                    className="block px-5 py-3 text-sm font-semibold text-accent border-t border-border bg-secondary/30 hover:bg-secondary/60 transition-colors text-center"
                  >
                    Alle Leistungen im Überblick →
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* 3. Über uns */}
          <div
            className="relative"
            onMouseEnter={() => openMenu("about")}
            onMouseLeave={scheduleClose}
          >
            <button
              type="button"
              onClick={() => setOpenDropdown((v) => (v === "about" ? null : "about"))}
              aria-expanded={openDropdown === "about"}
              aria-haspopup="true"
              className={dropdownTriggerClass}
            >
              Über uns
              <ChevronDown
                className={`w-4 h-4 transition-transform ${openDropdown === "about" ? "rotate-180" : ""}`}
              />
            </button>

            {openDropdown === "about" && (
              <div
                className="absolute top-full left-1/2 -translate-x-1/2 pt-3"
                onMouseEnter={() => openMenu("about")}
                onMouseLeave={scheduleClose}
              >
                <div className="w-[260px] rounded-2xl bg-card border border-border shadow-elegant overflow-hidden animate-fade-in p-2">
                  {aboutLinks.map((l) => (
                    <Link
                      key={l.to}
                      to={l.to}
                      className="block px-3 py-2.5 rounded-lg hover:bg-secondary/60 transition-colors group"
                    >
                      <div className="text-sm font-semibold text-primary group-hover:text-accent transition-colors">
                        {l.label}
                      </div>
                      <div className="text-xs text-muted-foreground line-clamp-1">{l.desc}</div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* 4. Empfehlung */}
          <Link to="/empfehlung" className={navLinkClass}>
            Empfehlung
          </Link>
        </nav>

        <div className="hidden md:block ml-6">
          <Button asChild variant="hero" size="default">
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
              Kostenlos Erstgespräch
            </a>
          </Button>
        </div>

        <button
          className={`md:hidden ml-auto p-2 rounded-md ${scrolled ? "text-primary" : "text-white"}`}
          onClick={() => setOpen((v) => !v)}
          aria-label="Menü öffnen"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <>
          <button
            type="button"
            aria-label="Menü schließen"
            onClick={() => setOpen(false)}
            className="md:hidden fixed inset-x-0 bottom-0 top-16 z-40 bg-black/20 backdrop-blur-[1px] animate-fade-in"
          />
        <div className="md:hidden relative z-50 bg-background border-t border-border animate-fade-in max-h-[calc(100dvh-8rem)] overflow-y-auto overscroll-contain">

          <div className="container py-4 pb-8 flex flex-col gap-1">
            <Link to="/" className="py-2 text-foreground font-medium" onClick={() => setOpen(false)}>
              Start
            </Link>

            <button
              type="button"
              onClick={() => setMobileServicesOpen((v) => !v)}
              className="flex items-center justify-between py-2 text-foreground font-medium"
              aria-expanded={mobileServicesOpen}
            >
              Leistungen
              <ChevronDown
                className={`w-4 h-4 transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`}
              />
            </button>
            {mobileServicesOpen && (
              <div className="pl-3 border-l border-border ml-1 mb-2 flex flex-col gap-3">
                {SERVICE_CATEGORIES.map((cat) => {
                  const items = getServicesByCategory(cat.name);
                  if (items.length === 0) return null;
                  return (
                    <div key={cat.name}>
                      <div className="text-[11px] font-bold uppercase tracking-wider text-accent mb-1">
                        {cat.name}
                      </div>
                      {items.map((s) => (
                        <Link
                          key={s.slug}
                          to={`/leistungen/${s.slug}`}
                          className="block py-1.5 text-sm text-foreground/80"
                          onClick={() => setOpen(false)}
                        >
                          {s.shortName}
                        </Link>
                      ))}
                    </div>
                  );
                })}
                <Link
                  to="/leistungen"
                  className="py-2 text-sm font-semibold text-accent"
                  onClick={() => setOpen(false)}
                >
                  Alle Leistungen ansehen →
                </Link>
              </div>
            )}

            <button
              type="button"
              onClick={() => setMobileAboutOpen((v) => !v)}
              className="flex items-center justify-between py-2 text-foreground font-medium"
              aria-expanded={mobileAboutOpen}
            >
              Über uns
              <ChevronDown
                className={`w-4 h-4 transition-transform ${mobileAboutOpen ? "rotate-180" : ""}`}
              />
            </button>
            {mobileAboutOpen && (
              <div className="pl-3 border-l border-border ml-1 mb-2 flex flex-col">
                {aboutLinks.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    className="py-2 text-sm text-foreground/80"
                    onClick={() => setOpen(false)}
                  >
                    {l.label}
                  </Link>
                ))}
              </div>
            )}

            <Link
              to="/empfehlung"
              className="py-2 text-foreground font-medium"
              onClick={() => setOpen(false)}
            >
              Empfehlung
            </Link>

            <Button asChild variant="hero" size="lg" className="mt-2">
              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                Kostenlos Erstgespräch
              </a>
            </Button>
          </div>
        </div>
        </>
      )}

    </header>
  );
}
